import React, { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCamera, 
  faUpload, 
  faTrash, 
  faTimes,
  faRedo,
  faCheck
} from '@fortawesome/free-solid-svg-icons';

/**
 * Componente para upload de foto com suporte a webcam e upload de arquivo
 * @param {string} currentPhotoUrl - URL da foto atual (se existir)
 * @param {function} onPhotoChange - Callback para quando a foto mudar (recebe File ou Blob)
 * @param {function} onPhotoRemove - Callback para quando a foto for removida
 * @param {string} label - Label do campo (padrão: "Foto")
 */
const FotoUpload = ({ 
  currentPhotoUrl = null, 
  onPhotoChange, 
  onPhotoRemove,
  label = "Foto"
}) => {
  const [showWebcam, setShowWebcam] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentPhotoUrl);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Atualizar preview quando currentPhotoUrl mudar
  useEffect(() => {
    console.log('📸 FotoUpload - currentPhotoUrl mudou:', currentPhotoUrl);
    setPreviewUrl(currentPhotoUrl);
  }, [currentPhotoUrl]);

  // Configurações da webcam
  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "user"
  };

  // Capturar foto da webcam
  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      // Converter base64 para Blob
      fetch(imageSrc)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], "webcam-photo.jpg", { type: "image/jpeg" });
          setCapturedPhoto(file);
          setPreviewUrl(imageSrc);
          setShowWebcam(false);
          
          if (onPhotoChange) {
            onPhotoChange(file);
          }
        });
    }
  }, [webcamRef, onPhotoChange]);

  // Upload de arquivo
  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem');
        return;
      }

      // Validar tamanho (máx 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 2MB');
        return;
      }

      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setCapturedPhoto(file);
        
        if (onPhotoChange) {
          onPhotoChange(file);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Remover foto
  const handleRemove = () => {
    setPreviewUrl(null);
    setCapturedPhoto(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    if (onPhotoRemove) {
      onPhotoRemove();
    }
  };

  // Cancelar captura da webcam
  const handleCancelWebcam = () => {
    setShowWebcam(false);
  };

  return (
    <div className="foto-upload-container">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      {/* Preview da foto */}
      {previewUrl && !showWebcam && (
        <div className="mb-4">
          <div className="relative inline-block">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300 cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => setShowPreviewModal(true)}
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
              title="Remover foto"
            >
              <FontAwesomeIcon icon={faTimes} size="xs" />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Clique na imagem para visualizar em tamanho maior
          </p>
        </div>
      )}

      {/* Webcam */}
      {showWebcam && (
        <div className="mb-4">
          <div className="relative inline-block">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              className="rounded-lg border-2 border-blue-500"
              style={{ width: '100%', maxWidth: '400px' }}
            />
          </div>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={handleCapture}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faCheck} />
              Capturar Foto
            </button>
            <button
              type="button"
              onClick={handleCancelWebcam}
              className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500 transition-colors flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faTimes} />
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Botões de ação */}
      {!showWebcam && !previewUrl && (
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => setShowWebcam(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faCamera} />
            Usar Webcam
          </button>
          
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faUpload} />
            Fazer Upload
          </button>
        </div>
      )}

      {!showWebcam && previewUrl && (
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => setShowWebcam(true)}
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <FontAwesomeIcon icon={faRedo} />
            Nova Foto (Webcam)
          </button>
          
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <FontAwesomeIcon icon={faUpload} />
            Novo Upload
          </button>
        </div>
      )}

      {/* Input file oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <p className="text-xs text-gray-500 mt-2">
        Formatos aceitos: JPG, PNG, GIF • Tamanho máximo: 2MB
      </p>

      {/* Modal de Preview */}
      {showPreviewModal && previewUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPreviewModal(false)}
        >
          <div className="bg-white rounded-lg max-w-4xl max-h-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium">{label}</h3>
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
            </div>
            <div className="p-4">
              <img
                src={previewUrl}
                alt="Preview"
                className="max-w-full max-h-96 mx-auto"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FotoUpload;
